import { debounce } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useGetBeneficiariesQuery } from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { NoResultsView } from '@/components/atoms/NoResultsView';
import { TableCellErrorBoundary } from '@/components/atoms/TableCellErrorBoundary';
import { TableContentSelector } from '@/components/atoms/TableContentSelector';
import { TableRowErrorBoundary } from '@/components/atoms/TableRowErrorBoundary';
import { HeaderCell } from '@/components/molecules/HeaderCell';
import { SearchBox } from '@/components/molecules/SearchBox';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useCustomer } from '@/hooks/useCustomer';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { BeneficiariesFilters } from '@/Screens/Dashboard/Beneficiaries/BeneficiariesFilters';
import { authSelector } from '@/StateManagement/AuthReducer';
import { Beneficiary, BeneficiaryApiResponse } from '@/types/beneficiary';
import { GetBeneficiariesFilters } from '@/types/getBeneficiariesFilters';
import { HeaderItem } from '@/types/headerItem';
import { Page } from '@/types/page';
import { computeSortingValue, parseSort } from '@/utility/sorting';
import { toBeneficiary } from '@/utility/toBeneficiary';

export const Beneficiaries: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const navigate = useNavigate();
  const customer = useCustomer();
  const { user } = useSelector(authSelector);
  const dateFormatter = useDateFormatter('MMM d, yyyy | HH:mm');

  const orgName =
    customer.business?.name ||
    customer.businessInfo?.name ||
    user?.businessName ||
    customer.firstName ||
    '';
  const ownBeneficiaryId = customer.beneficiaryId;

  const [filters, setFilters] = useTypedSearchParams<GetBeneficiariesFilters>(
    GetBeneficiariesFilters.initial(),
  );
  const [filtersModalOpen, setFiltersModalOpen] = React.useState<boolean>(false);

  const queryFilters = React.useMemo(
    (): GetBeneficiariesFilters => ({
      ...filters,
      fromDate: toUtcStartOfDay(filters.fromDate),
      toDate: toUtcEndOfDay(filters.toDate),
    }),
    [filters],
  );

  const { data = Page.empty<BeneficiaryApiResponse>(), status } =
    useGetBeneficiariesQuery(queryFilters);

  const {
    content: beneficiaries,
    totalPages,
    totalElements,
    pageable: { pageNumber },
  } = data;

  const rows = React.useMemo(
    (): readonly Beneficiary[] =>
      beneficiaries
        .filter((b: BeneficiaryApiResponse): boolean => b.id !== ownBeneficiaryId)
        .map(toBeneficiary),
    [beneficiaries, ownBeneficiaryId],
  );

  const headers = React.useMemo((): readonly HeaderItem<Beneficiary>[] => BENEFICIARY_HEADERS, []);

  const sorting = React.useMemo((): Record<string, 'asc' | 'desc'> => {
    const sort = filters.sort;
    const list = typeof sort === 'string' ? [sort] : sort;

    if (list.length > 0) {
      return parseSort(list);
    }

    return { createdDate: 'desc' };
  }, [filters.sort]);

  const filtersCount = React.useMemo((): number => countFilters(filters), [filters]);

  const handleSort = React.useCallback(
    (column: string): void => {
      setFilters((current: GetBeneficiariesFilters): GetBeneficiariesFilters => {
        const sort = typeof current.sort === 'string' ? [current.sort] : current.sort;

        const existing = sort
          .map((item: string): [string, string] => {
            const [targetColumn, direction] = item.split(',');
            return [targetColumn, direction];
          })
          .filter(
            (item: [string, string]): item is [string, 'asc' | 'desc'] =>
              item[1] === 'asc' || item[1] === 'desc',
          )
          .find(([targetColumn]: [string, 'asc' | 'desc']): boolean => targetColumn === column);

        if (!existing) {
          return {
            ...current,
            sort: [`${column},asc`, ...sort],
            page: 1,
          };
        }

        const [, direction] = existing;
        const nextDirection = computeSortingValue(direction);

        return {
          ...current,
          sort: [
            `${column},${nextDirection}`,
            ...sort.filter((item: string): boolean => item.split(',')?.[0] !== column),
          ],
          page: 1,
        };
      });
    },
    [setFilters],
  );

  const onRowClick = React.useCallback(
    (beneficiaryId: string): void => {
      navigate(beneficiaryId);
    },
    [navigate],
  );

  const onOrgClick = React.useCallback((): void => {
    navigate('org');
  }, [navigate]);

  const handleSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: search } = event.target;
      setFilters(
        (current: GetBeneficiariesFilters): GetBeneficiariesFilters => ({
          ...current,
          name: search.trim() || undefined,
          page: 1,
        }),
      );
    },
    [setFilters],
  );

  const debouncedHandleSearch = React.useMemo(
    (): ReturnType<typeof debounce> => debounce(handleSearch, 300),
    [handleSearch],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      debouncedHandleSearch.clear();
    };
  }, [debouncedHandleSearch]);

  const handlePageChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, nextPage: number): void => {
      setFilters(
        (current: GetBeneficiariesFilters): GetBeneficiariesFilters => ({
          ...current,
          page: nextPage,
        }),
      );
    },
    [setFilters],
  );

  const openFiltersModal = React.useCallback((): void => {
    setFiltersModalOpen(true);
  }, []);

  const onFiltersModalClose = React.useCallback((): void => {
    setFiltersModalOpen(false);
  }, []);

  const handleFiltersChange = React.useCallback(
    (next: GetBeneficiariesFilters): void => {
      setFilters({ ...next, page: 1 });
      setFiltersModalOpen(false);
    },
    [setFilters],
  );

  return (
    <MainScreen title={tr('ACCOUNTS')}>
      <Box display="flex" flexDirection="column" gap={4} height="100%">
        <Typography variant="body1" color="text.secondary" mt={-3}>
          {tr('ACCOUNTS_SUBTITLE')}
        </Typography>

        <Stack gap={2}>
          <Typography variant="h6">{tr('YOUR_ORGANIZATION')}</Typography>
          <ButtonBase sx={styles.orgCard} onClick={onOrgClick}>
            <Stack direction="row" alignItems="center" gap={3} flex={1}>
              <Box sx={styles.orgIconCircle}>
                <Typography>&#9733;</Typography>
              </Box>
              <Stack alignItems="flex-start">
                <Typography variant="h6">{orgName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {tr('MASTER_ACCOUNT')}
                </Typography>
              </Stack>
            </Stack>
          </ButtonBase>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} mt={4}>
          <Typography variant="h6">{tr('CUSTOMER_ACCOUNTS')}</Typography>
          <Stack direction="row" gap={2}>
            <SearchBox placeholder={tr('SEARCH_BY_NAME')} onChange={debouncedHandleSearch} />
            <Button variant="light" size="compact" onClick={openFiltersModal}>
              {filtersCount === 0 ? tr('FILTER') : tr('FILTER_WITH_COUNT', filtersCount)}
            </Button>
          </Stack>
        </Stack>
        <Paper elevation={3} sx={styles.paper}>
          <TableContentSelector
            status={status}
            data={rows}
            filtersCount={filtersCount + ((filters.name ?? '').trim() ? 1 : 0)}
            noContentView={
              <NoResultsView
                title={tr('NO_BENEFICIARIES_YET_TITLE')}
                message={tr('NO_BENEFICIARIES_YET_MESSAGE')}
              />
            }
            noResultsView={
              <NoResultsView
                title={tr('ZERO_RESULTS_FOUND')}
                message={tr(
                  'TRY_CHANGING_YOUR_APPLIED_FILTERS',
                  tr('BENEFICIARIES').toLocaleLowerCase(),
                )}
              />
            }>
            <Table sx={styles.table}>
              <colgroup>
                {widths.map(
                  (width: number, index: number): React.ReactElement => (
                    <col key={index} width={width / totalWidth} />
                  ),
                )}
              </colgroup>
              <TableHead>
                <TableRow>
                  {headers.map(
                    (item: HeaderItem<Beneficiary>): React.ReactElement => (
                      <HeaderCell
                        key={item.key}
                        item={item}
                        sorting={sorting}
                        onClick={handleSort}
                      />
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(
                  (row: Beneficiary): React.ReactElement => (
                    <TableRowErrorBoundary
                      key={row.id}
                      columnCount={3}
                      fallbackMessage="Unable to display beneficiary">
                      <Clickable data={row.id} onClick={onRowClick}>
                        <TableRow sx={styles.row} hover>
                          <TableCellErrorBoundary sx={styles.cell} fallbackMessage="N/A">
                            <TableCell sx={styles.cell}>{row.name}</TableCell>
                          </TableCellErrorBoundary>
                          <TableCellErrorBoundary sx={styles.cell} fallbackMessage="N/A">
                            <TableCell sx={styles.cell}>{tr(row.type)}</TableCell>
                          </TableCellErrorBoundary>
                          <TableCellErrorBoundary sx={styles.cell} fallbackMessage="N/A">
                            <TableCell sx={styles.cell}>
                              {dateFormatter.parseAndFormat(row.createdDate)}
                            </TableCell>
                          </TableCellErrorBoundary>
                        </TableRow>
                      </Clickable>
                    </TableRowErrorBoundary>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContentSelector>
        </Paper>
        {totalElements > 0 && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Pagination count={totalPages} page={pageNumber + 1} onChange={handlePageChange} />
          </Box>
        )}
      </Box>

      {filtersModalOpen && (
        <ApplicationDialog onClose={onFiltersModalClose}>
          <ApplicationModal
            title={tr('FILTER')}
            description={tr('SELECT_FILTERS_THAT_APPLY')}
            onClose={onFiltersModalClose}>
            <BeneficiariesFilters filters={filters} onChange={handleFiltersChange} />
          </ApplicationModal>
        </ApplicationDialog>
      )}
    </MainScreen>
  );
};

const COLUMN_WIDTHS = {
  NAME: 300,
  TYPE: 150,
  CREATED: 200,
} as const;

const widths = Object.values(COLUMN_WIDTHS);
const totalWidth = widths.reduce((total: number, width: number): number => total + width, 0);

const BENEFICIARY_HEADERS: readonly HeaderItem<Beneficiary>[] = [
  { label: 'NAME', key: 'name', sortable: false },
  { label: 'TYPE', key: 'type', sortable: false },
  { label: 'CREATED', key: 'createdDate', sortable: true },
];

// Reinterprets the date portion of an ISO datetime as start-of-day UTC.
// The filter form stores dates in the user's timezone; the backend expects UTC midnight.
const toUtcStartOfDay = (isoDateTime: string | undefined): string | undefined => {
  if (!isoDateTime) {
    return undefined;
  }
  return `${isoDateTime.slice(0, 10)}T00:00:00.000Z`;
};

const toUtcEndOfDay = (isoDateTime: string | undefined): string | undefined => {
  if (!isoDateTime) {
    return undefined;
  }
  return `${isoDateTime.slice(0, 10)}T23:59:59.999Z`;
};

const countFilters = (filters: GetBeneficiariesFilters): number => {
  return Object.entries(filters).reduce(
    (totalCount: number, [key, value]: [string, unknown]): number => {
      switch (key) {
        case 'type':
        case 'fromDate':
        case 'toDate':
          return totalCount + (value !== undefined ? 1 : 0);
        default:
          return totalCount;
      }
    },
    0,
  );
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    flex: 1,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  cell: {
    minWidth: 0,
    py: 2,
    '&:first-of-type': {
      pl: 5,
    },
    '&:last-of-type': {
      pr: 5,
    },
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  row: {
    cursor: 'pointer',
  },
  orgCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 4,
    py: 2,
    borderRadius: 2,
    backgroundColor: 'custom.Primary1Background',
    width: '100%',
  },
  orgIconCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: 'custom.White',
    color: 'primary.main',
  },
};
